import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { UserPreference, UserPreferenceLabel } from '@completion/enums';
import { getCalendarConsentGranted, getCurrentUserPreferences } from '@completion/selectors';
import { UserPreferenceService } from '@completion/services';
import { SaveCalendarConsent } from '@completion/actions';

@Component({
  selector: 'app-user-preference-dialog',
  templateUrl: './user-preference-dialog.component.html',
  styleUrls: ['./user-preference-dialog.component.scss'],
  standalone: false,
})
export class UserPreferenceDialogComponent implements OnInit {
  readonly userPreference = UserPreference;
  readonly userPreferenceLabel = UserPreferenceLabel;
  useFlatTagTableView = false;

  /** Three-state calendar consent: null = not set, true = consented, false = declined */
  calendarConsentGranted: boolean | null = null;

  /** Whether the user has been warned about changing consent this session */
  showConsentWarning = false;
  pendingConsentValue: boolean | null = null;

  constructor(
    private dialogRef: MatDialogRef<UserPreferenceDialogComponent>,
    private store: Store,
    private readonly userPreferenceService: UserPreferenceService,
  ) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentUserPreferences)
      .pipe(take(1))
      .subscribe((preferences) => {
        // preferences are UserPreferenceEntry[] — extract the name for the service
        this.userPreferenceService.initializeFromServer((preferences as unknown as Array<{ name: UserPreference }>).map((p) => p.name));
        this.useFlatTagTableView = this.userPreferenceService.isEnabled(UserPreference.USE_FLAT_TAG_TABLE_VIEW);
      });

    this.store
      .select(getCalendarConsentGranted)
      .pipe(take(1))
      .subscribe((consent) => {
        this.calendarConsentGranted = consent;
      });
  }

  onFlatTagTableToggle(enabled: boolean): void {
    this.useFlatTagTableView = enabled;
    this.userPreferenceService.setPreference(UserPreference.USE_FLAT_TAG_TABLE_VIEW, enabled);
  }

  /** Called when the user clicks the consent toggle. Shows a warning before applying. */
  onConsentToggleRequest(newValue: boolean): void {
    this.pendingConsentValue = newValue;
    this.showConsentWarning = true;
  }

  /** User confirmed the consent change in the warning panel. */
  confirmConsentChange(): void {
    if (this.pendingConsentValue === null) {
      return;
    }
    this.calendarConsentGranted = this.pendingConsentValue;
    this.store.dispatch(new SaveCalendarConsent(this.pendingConsentValue));
    this.dismissConsentWarning();
  }

  dismissConsentWarning(): void {
    this.showConsentWarning = false;
    this.pendingConsentValue = null;
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
